import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, doc, onSnapshot, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';

// Book Log Creation Form Component
export const BookLogCreationForm = ({
  newBookLog,
  setNewBookLog,
  handleAddBookLog,
  bookLists = []
}) => {
  return (
    <div className="create-post">
      <h2>Log New Book</h2>
      <input
        type="text"
        placeholder="Book Title"
        value={newBookLog.title}
        onChange={(e) => setNewBookLog({ ...newBookLog, title: e.target.value })}
        className="post-title-input"
      />
      <input
        type="text"
        placeholder="Author"
        value={newBookLog.author}
        onChange={(e) => setNewBookLog({ ...newBookLog, author: e.target.value })}
        className="post-title-input"
      />
      <div className="form-row">
        <div className="form-group">
          <label>Rating (out of 5):</label>
          <select
            value={newBookLog.rating}
            onChange={(e) => setNewBookLog({ ...newBookLog, rating: e.target.value })}
            className="project-type-dropdown"
          >
            <option value="">Select Rating</option>
            <option value="1">1 - Poor</option>
            <option value="2">2 - Fair</option>
            <option value="3">3 - Good</option>
            <option value="4">4 - Very Good</option>
            <option value="5">5 - Excellent</option>
          </select>
        </div>
      </div>
      <div className="form-group">
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', fontStyle: 'italic', marginTop: '8px' }}>
          Books will be automatically matched to reading lists based on title and author.
        </p>
      </div>
      <textarea
        placeholder="Review (optional)..."
        value={newBookLog.review}
        onChange={(e) => setNewBookLog({ ...newBookLog, review: e.target.value })}
        className="post-content-input"
        rows="5"
      />
      <button className="log-activity-button" onClick={handleAddBookLog}>Log Book</button>
    </div>
  );
};

// Book Log Display Component
export const BookLogDisplay = ({
  bookLogs,
  currentUser,
  studentId,
  hideHeader,
  bookLists = [],
  // Optional editing props
  editingBookLogId,
  editBookLogForm,
  setEditBookLogForm,
  handleStartEditBookLog,
  handleSaveBookLogEdit,
  handleCancelEditBookLog,
  handleDeleteBookLog
}) => {
  // State for tracking expanded book lists
  const [expandedBookLists, setExpandedBookLists] = useState(new Set());
  
  const toggleBookListExpansion = (listId) => {
    setExpandedBookLists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };
  
  const isBookListExpanded = (listId) => {
    return expandedBookLists.has(listId);
  };
  
  const viewingUserId = studentId || currentUser.uid;
  const userBookLogs = bookLogs.filter(log => log.userId === viewingUserId);
  
  if (userBookLogs.length === 0) {
    return <p className="no-posts">No books logged yet. Start logging your books above!</p>;
  }

  // Group logs by reading list
  const groupBookLogsByReadingList = (logs) => {
    const grouped = {};
    const ungroupedLogs = [];
    
    logs.forEach(log => {
      if (log.bookListIds && log.bookListIds.length > 0) {
        // Add to each reading list the book belongs to
        log.bookListIds.forEach(listId => {
          if (!grouped[listId]) {
            grouped[listId] = [];
          }
          grouped[listId].push(log);
        });
      } else {
        // Books without a reading list
        ungroupedLogs.push(log);
      }
    });
    
    // Create result array
    const result = [];
    
    // Add grouped reading lists
    Object.keys(grouped).forEach(listId => {
      const bookList = bookLists.find(l => l.id === listId);
      if (bookList) {
        result.push({
          listId: listId,
          listName: bookList.name,
          logs: grouped[listId].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        });
      }
    });
    
    // Sort reading lists alphabetically by name
    result.sort((a, b) => a.listName.localeCompare(b.listName));
    
    // Add "My Books" section at the end if there are ungrouped books
    if (ungroupedLogs.length > 0) {
      result.push({
        listId: null,
        listName: 'My Books',
        logs: ungroupedLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      });
    }
    
    return result;
  };
  
  const logsByReadingList = groupBookLogsByReadingList(userBookLogs);

  return (
    <div className="time-logs-container time-logging-section">
      {!hideHeader && (
        <>
          <h2>Your Book Logs</h2>
          <p>Track the books you've read and share your thoughts.</p>
        </>
      )}
      
      <div>
        {/* Reading List-based Organization */}
        {logsByReadingList.map(({ listId, listName, logs }) => {
          const listKey = listId || 'my-books';
          const isExpanded = isBookListExpanded(listKey);
          
          return (
            <div key={listKey} className="book-list-section">
              <div 
                className="book-list-section-header"
                onClick={() => toggleBookListExpansion(listKey)}
              >
                <div className={`book-list-section-arrow ${isExpanded ? 'expanded' : ''}`}>
                  ▶
                </div>
                <h4 className="book-list-section-title">
                  {listName}
                  <span className="book-list-section-count">
                    ({logs.length} {logs.length === 1 ? 'book' : 'books'})
                  </span>
                </h4>
              </div>

              {/* Book Logs for this List */}
              {isExpanded && (
              <div className="time-logs-list">
                {logs.map(bookLog => {
                  const isEditing = editingBookLogId === bookLog.id;
                  return (
                    <div key={bookLog.id} className="consolidated-time-log-entry">
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            className="post-title-input"
                            value={editBookLogForm?.title || ''}
                            onChange={(e) => setEditBookLogForm && setEditBookLogForm({ ...editBookLogForm, title: e.target.value })}
                            placeholder="Book Title"
                          />
                          <input
                            type="text"
                            className="post-title-input"
                            value={editBookLogForm?.author || ''}
                            onChange={(e) => setEditBookLogForm && setEditBookLogForm({ ...editBookLogForm, author: e.target.value })}
                            placeholder="Author"
                          />
                          <div className="form-row">
                            <div className="form-group">
                              <label>Rating:</label>
                              <select
                                value={editBookLogForm?.rating || ''}
                                onChange={(e) => setEditBookLogForm && setEditBookLogForm({ ...editBookLogForm, rating: e.target.value })}
                                className="project-type-dropdown"
                              >
                                <option value="">Select Rating</option>
                                <option value="1">1 - Poor</option>
                                <option value="2">2 - Fair</option>
                                <option value="3">3 - Good</option>
                                <option value="4">4 - Very Good</option>
                                <option value="5">5 - Excellent</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-group">
                            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', fontStyle: 'italic', marginTop: '8px' }}>
                              Books will be automatically matched to reading lists based on title and author when saved.
                            </p>
                          </div>
                          <textarea
                            value={editBookLogForm?.review || ''}
                            onChange={(e) => setEditBookLogForm && setEditBookLogForm({ ...editBookLogForm, review: e.target.value })}
                            className="proposal-edit-textarea"
                            rows="5"
                            placeholder="Review..."
                          />
                          <div className="proposal-actions">
                            <button className="proposal-save-btn" onClick={handleSaveBookLogEdit}>Save</button>
                            <button className="proposal-cancel-btn" onClick={handleCancelEditBookLog}>Cancel</button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="consolidated-time-log-main">
                            <div className="book-log-title-row">
                              <h5>{bookLog.title}</h5>
                              {bookLog.rating && (
                                <div className="book-rating-inline">
                                  <span className="rating-stars">
                                    {Array.from({ length: parseInt(bookLog.rating) }, (_, i) => (
                                      <i key={`filled-${i}`} className="fa-solid fa-star"></i>
                                    ))}
                                    {Array.from({ length: 5 - parseInt(bookLog.rating) }, (_, i) => (
                                      <i key={`empty-${i}`} className="fa-regular fa-star"></i>
                                    ))}
                                  </span>
                                  <span> ({bookLog.rating}/5)</span>
                                </div>
                              )}
                            </div>
                            {bookLog.author && (
                              <p className="book-author-text"><em>by {bookLog.author}</em></p>
                            )}
                            {bookLog.publicationDate && (
                              <span className="individual-dates">
                                Published: {bookLog.publicationDate}
                              </span>
                            )}
                          </div>
                          {bookLog.review && (
                            <p className="individual-description">{bookLog.review}</p>
                          )}
                          <div className="individual-time-log-header">
                            <div className="book-log-meta-left">
                              <span className="individual-dates">
                                Logged: {new Date(bookLog.timestamp).toLocaleDateString()}
                              </span>
                              {bookLog.bookListIds && bookLog.bookListIds.length > 0 && (
                                <div className="book-list-tags-inline">
                                  {bookLog.bookListIds.map(listId => {
                                    const list = bookLists.find(l => l.id === listId);
                                    return list ? (
                                      <span key={listId} className="book-list-tag">{list.name}</span>
                                    ) : null;
                                  })}
                                </div>
                              )}
                            </div>
                            {currentUser && bookLog.userId === currentUser.uid && (
                              <div className="proposal-actions">
                                <button className="proposal-edit-btn" onClick={() => handleStartEditBookLog && handleStartEditBookLog(bookLog)}>Edit</button>
                                <button className="proposal-reject-btn" onClick={() => handleDeleteBookLog && handleDeleteBookLog(bookLog.id)}>Delete</button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Book Logging Hook
export const useBookLogging = ({ currentUser, userProfile, bookLists = [] }) => {
  const [bookLogs, setBookLogs] = useState([]);
  const [newBookLog, setNewBookLog] = useState({ 
    title: '', 
    author: '', 
    rating: '', 
    review: '',
    bookListIds: []
  });
  const [editingBookLogId, setEditingBookLogId] = useState(null);
  const [editBookLogForm, setEditBookLogForm] = useState({ 
    title: '', 
    author: '', 
    rating: '', 
    review: '',
    bookListIds: []
  });

  // Listen for Book Logs
  useEffect(() => {
    if (!currentUser) {
      setBookLogs([]);
      return;
    }

    const qBookLogs = query(collection(db, 'bookLogs'), orderBy('timestamp', 'desc'));
    const unsubscribeBookLogs = onSnapshot(qBookLogs, (snapshot) => {
      const bookLogsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBookLogs(bookLogsData);
    }, (error) => console.error("Error fetching book logs:", error));

    return () => unsubscribeBookLogs();
  }, [currentUser]);

  // Helper function to automatically match book to reading lists
  const findMatchingBookLists = (title, author) => {
    if (!title || !author || bookLists.length === 0) {
      return [];
    }

  // Helper functions for matching (handles capitalization differences)
  const normalizeText = (text) => {
    if (!text) return '';
    return String(text).toLowerCase().trim().replace(/\s+/g, ' ');
  };

  const normalizeAuthor = (author) => {
    if (!author) return '';
    const normalized = normalizeText(author);
    return normalized.split(/[, ]+/).filter(part => part.length > 0);
  };

    const authorsMatch = (author1Parts, author2Parts) => {
      if (author1Parts.length === 0 || author2Parts.length === 0) return false;
      const allParts1In2 = author1Parts.every(part => 
        author2Parts.some(part2 => part2.includes(part) || part.includes(part2))
      );
      const allParts2In1 = author2Parts.every(part => 
        author1Parts.some(part1 => part1.includes(part) || part.includes(part1))
      );
      return allParts1In2 || allParts2In1;
    };

    const booksMatch = (book1, book2) => {
      // Normalize both titles and authors to handle capitalization differences
      const title1 = normalizeText(String(book1.title || '').trim());
      const title2 = normalizeText(String(book2.title || '').trim());
      const author1Parts = normalizeAuthor(String(book1.author || '').trim());
      const author2Parts = normalizeAuthor(String(book2.author || '').trim());
      
      // Titles must match exactly (case-insensitive, normalized)
      if (title1 !== title2) return false;
      
      // Authors must match (flexible partial matching)
      return authorsMatch(author1Parts, author2Parts);
    };

    const matchingListIds = [];
    const currentBook = { title: title.trim(), author: author.trim() };

    bookLists.forEach(bookList => {
      if (bookList.books && bookList.books.length > 0) {
        const matches = bookList.books.some(listBook => booksMatch(currentBook, listBook));
        if (matches) {
          matchingListIds.push(bookList.id);
        }
      }
    });

    return matchingListIds;
  };

  const handleAddBookLog = async () => {
    if (!newBookLog.title.trim() || !newBookLog.author.trim() || !currentUser) {
      alert('Please fill in the title and author.');
      return;
    }

    // Automatically find matching book lists
    const matchedBookListIds = findMatchingBookLists(newBookLog.title.trim(), newBookLog.author.trim());

    try {
      await addDoc(collection(db, 'bookLogs'), {
        userId: currentUser.uid,
        title: newBookLog.title.trim(),
        author: newBookLog.author.trim(),
        rating: newBookLog.rating ? parseInt(newBookLog.rating) : null,
        review: newBookLog.review.trim() || '',
        bookListIds: matchedBookListIds, // Automatically assigned
        grade: userProfile.grade, // Include student's grade from profile
        timestamp: new Date().toISOString()
      });

      // Clear the form
      setNewBookLog({ 
        title: '', 
        author: '', 
        rating: '', 
        review: '',
        bookListIds: []
      });
    } catch (error) {
      console.error("Error adding book log:", error.message);
      alert('Failed to add book log entry. Please try again.');
    }
  };

  const handleStartEditBookLog = (bookLog) => {
    setEditingBookLogId(bookLog.id);
    setEditBookLogForm({
      title: bookLog.title || '',
      author: bookLog.author || '',
      rating: bookLog.rating ? String(bookLog.rating) : '',
      review: bookLog.review || '',
      bookListIds: bookLog.bookListIds || []
    });
  };

  const handleSaveBookLogEdit = async () => {
    if (!editingBookLogId) return;
    try {
      // Automatically find matching book lists based on edited title/author
      const matchedBookListIds = findMatchingBookLists(editBookLogForm.title.trim(), editBookLogForm.author.trim());
      
      const ref = doc(db, 'bookLogs', editingBookLogId);
      const updates = {
        title: editBookLogForm.title.trim(),
        author: editBookLogForm.author.trim(),
        rating: editBookLogForm.rating ? parseInt(editBookLogForm.rating) : null,
        review: editBookLogForm.review.trim() || '',
        bookListIds: matchedBookListIds // Automatically updated
      };
      await updateDoc(ref, updates);
      setBookLogs(prev => prev.map(b => b.id === editingBookLogId ? { ...b, ...updates } : b));
      setEditingBookLogId(null);
      setEditBookLogForm({ title: '', author: '', rating: '', review: '', bookListIds: [] });
    } catch (error) {
      console.error('Error saving book log edit:', error);
      alert('Failed to save changes.');
    }
  };

  const handleCancelEditBookLog = () => {
    setEditingBookLogId(null);
    setEditBookLogForm({ title: '', author: '', rating: '', review: '', bookListIds: [] });
  };

  const handleDeleteBookLog = async (bookLogId) => {
    try {
      const confirmDelete = window.confirm('Delete this book log? This cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'bookLogs', bookLogId));
      setBookLogs(prev => prev.filter(b => b.id !== bookLogId));
    } catch (error) {
      console.error('Error deleting book log:', error);
      alert('Failed to delete book log.');
    }
  };

  return {
    bookLogs,
    setBookLogs,
    newBookLog,
    setNewBookLog,
    handleAddBookLog,
    editingBookLogId,
    editBookLogForm,
    setEditBookLogForm,
    handleStartEditBookLog,
    handleSaveBookLogEdit,
    handleCancelEditBookLog,
    handleDeleteBookLog
  };
};

// Utility Functions
export const bookLoggingUtils = {
  groupBookLogsByGrade: (logs) => {
    const grouped = {};
    logs.forEach(log => {
      const grade = log.grade || 'Unknown';
      if (!grouped[grade]) {
        grouped[grade] = [];
      }
      grouped[grade].push(log);
    });
    
    // Sort grades with Portfolio Defense first, then ascending order (8th grade first), Unknown last
    const sortedGrades = Object.keys(grouped).sort((a, b) => {
      // Portfolio Defense goes first
      if (a === 'Portfolio Defense') return -1;
      if (b === 'Portfolio Defense') return 1;
      // Handle 'Unknown' grade by putting it last
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return parseInt(a) - parseInt(b);
    });
    
    return sortedGrades.map(grade => ({
      grade: grade,
      logs: grouped[grade].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Sort by newest first
    }));
  }
};

// Book List Management Hook
export const useBookListManagement = ({ currentUser }) => {
  const [bookLists, setBookLists] = useState([]);
  const [newBookList, setNewBookList] = useState({ name: '', description: '', books: [] });
  const [editingBookListId, setEditingBookListId] = useState(null);
  const [editBookListForm, setEditBookListForm] = useState({ name: '', description: '', books: [] });
  const [newBookEntry, setNewBookEntry] = useState({ title: '', author: '' });

  // Listen for Book Lists
  useEffect(() => {
    if (!currentUser) {
      setBookLists([]);
      return;
    }

    const qBookLists = query(collection(db, 'bookLists'), orderBy('createdAt', 'desc'));
    const unsubscribeBookLists = onSnapshot(qBookLists, (snapshot) => {
      const bookListsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBookLists(bookListsData);
    }, (error) => console.error("Error fetching book lists:", error));

    return () => unsubscribeBookLists();
  }, [currentUser]);

  const handleAddBookList = async () => {
    if (!newBookList.name.trim()) {
      alert('Please provide a book list name.');
      return;
    }

    try {
      await addDoc(collection(db, 'bookLists'), {
        name: newBookList.name.trim(),
        description: newBookList.description.trim() || '',
        books: newBookList.books || [],
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });

      setNewBookList({ name: '', description: '', books: [] });
      setNewBookEntry({ title: '', author: '' });
    } catch (error) {
      console.error("Error adding book list:", error.message);
      alert('Failed to add book list. Please try again.');
    }
  };

  const handleAddBookToList = () => {
    if (!newBookEntry.title.trim() || !newBookEntry.author.trim()) {
      alert('Please provide both title and author.');
      return;
    }

    const book = {
      title: newBookEntry.title.trim(),
      author: newBookEntry.author.trim(),
      id: Date.now().toString() // Simple ID for list management
    };

    setNewBookList({ ...newBookList, books: [...(newBookList.books || []), book] });
    setNewBookEntry({ title: '', author: '' });
  };

  const handleRemoveBookFromList = (bookId) => {
    setNewBookList({ ...newBookList, books: (newBookList.books || []).filter(b => b.id !== bookId) });
  };

  const handleStartEditBookList = (bookList) => {
    setEditingBookListId(bookList.id);
    setEditBookListForm({
      name: bookList.name || '',
      description: bookList.description || '',
      books: bookList.books || []
    });
    setNewBookEntry({ title: '', author: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddBookToEditList = () => {
    if (!newBookEntry.title.trim() || !newBookEntry.author.trim()) {
      alert('Please provide both title and author.');
      return;
    }

    const book = {
      title: newBookEntry.title.trim(),
      author: newBookEntry.author.trim(),
      id: Date.now().toString()
    };

    setEditBookListForm({ ...editBookListForm, books: [...(editBookListForm.books || []), book] });
    setNewBookEntry({ title: '', author: '' });
  };

  const handleRemoveBookFromEditList = (bookId) => {
    setEditBookListForm({ ...editBookListForm, books: (editBookListForm.books || []).filter(b => b.id !== bookId) });
  };

  const handleSaveBookListEdit = async () => {
    if (!editingBookListId || !editBookListForm.name.trim()) {
      alert('Please provide a book list name.');
      return;
    }

    try {
      const ref = doc(db, 'bookLists', editingBookListId);
      await updateDoc(ref, {
        name: editBookListForm.name.trim(),
        description: editBookListForm.description.trim() || '',
        books: editBookListForm.books || []
      });
      setEditingBookListId(null);
      setEditBookListForm({ name: '', description: '', books: [] });
      setNewBookEntry({ title: '', author: '' });
    } catch (error) {
      console.error('Error saving book list edit:', error);
      alert('Failed to save changes.');
    }
  };

  const handleCancelEditBookList = () => {
    setEditingBookListId(null);
    setEditBookListForm({ name: '', description: '', books: [] });
    setNewBookEntry({ title: '', author: '' });
  };

  const handleDeleteBookList = async (bookListId) => {
    try {
      const confirmDelete = window.confirm('Delete this book list? This cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'bookLists', bookListId));
    } catch (error) {
      console.error('Error deleting book list:', error);
      alert('Failed to delete book list.');
    }
  };

  return {
    bookLists,
    newBookList,
    setNewBookList,
    handleAddBookList,
    editingBookListId,
    editBookListForm,
    setEditBookListForm,
    handleStartEditBookList,
    handleSaveBookListEdit,
    handleCancelEditBookList,
    handleDeleteBookList,
    newBookEntry,
    setNewBookEntry,
    handleAddBookToList,
    handleRemoveBookFromList,
    handleAddBookToEditList,
    handleRemoveBookFromEditList
  };
};

// Book List Management View Component (for teachers)
export const BookListManagementView = ({
  bookLists,
  newBookList,
  setNewBookList,
  handleAddBookList,
  editingBookListId,
  editBookListForm,
  setEditBookListForm,
  handleStartEditBookList,
  handleSaveBookListEdit,
  handleCancelEditBookList,
  handleDeleteBookList,
  newBookEntry,
  setNewBookEntry,
  handleAddBookToList,
  handleRemoveBookFromList,
  handleAddBookToEditList,
  handleRemoveBookFromEditList
}) => {
  const [bulkBooksInput, setBulkBooksInput] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  
  // Reset bulk import when switching between create/edit modes
  useEffect(() => {
    setBulkBooksInput('');
    setShowBulkImport(false);
  }, [editingBookListId]);
  
  const isEditing = editingBookListId !== null;
  const currentForm = isEditing ? editBookListForm : newBookList;
  const currentBooks = currentForm.books || [];
  const handleAddBook = isEditing ? handleAddBookToEditList : handleAddBookToList;
  const handleRemoveBook = isEditing ? handleRemoveBookFromEditList : handleRemoveBookFromList;

  const handleBulkImportBooks = () => {
    if (!bulkBooksInput.trim()) {
      alert('Please enter books to import.');
      return;
    }

    const lines = bulkBooksInput.split('\n').filter(line => line.trim().length > 0);
    const newBooks = [];
    const errors = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      // Try to parse "Title, Author" format
      const commaIndex = trimmedLine.indexOf(',');
      
      if (commaIndex === -1) {
        errors.push(`Line ${index + 1}: Missing comma separator. Format should be "Title, Author"`);
        return;
      }

      const title = trimmedLine.substring(0, commaIndex).trim();
      const author = trimmedLine.substring(commaIndex + 1).trim();

      if (!title || !author) {
        errors.push(`Line ${index + 1}: Missing title or author`);
        return;
      }

      newBooks.push({
        title: title,
        author: author,
        id: Date.now().toString() + '-' + index // Unique ID
      });
    });

    if (errors.length > 0) {
      alert('Some books could not be imported:\n\n' + errors.join('\n'));
      if (newBooks.length === 0) {
        return;
      }
    }

    // Add all successfully parsed books
    if (isEditing) {
      setEditBookListForm({ ...editBookListForm, books: [...(editBookListForm.books || []), ...newBooks] });
    } else {
      setNewBookList({ ...newBookList, books: [...(newBookList.books || []), ...newBooks] });
    }

    setBulkBooksInput('');
    setShowBulkImport(false);
    
    if (newBooks.length > 0) {
      alert(`Successfully imported ${newBooks.length} book${newBooks.length === 1 ? '' : 's'}!`);
    }
  };

  return (
    <div className="post">
      <h2>Book List Management</h2>
      <p className="transcript-description">
        Create and manage reading lists for students. Students can tag their logged books with these lists to track their progress.
      </p>

      {/* Create/Edit Book List Form */}
      <div className="create-post">
        <h3>{isEditing ? 'Edit Book List' : 'Create New Book List'}</h3>
        <input
          type="text"
          placeholder="Book List Name (e.g., 'Summer Reading 2024', 'Classic Literature')"
          value={currentForm.name}
          onChange={(e) => {
            if (isEditing) {
              setEditBookListForm({ ...editBookListForm, name: e.target.value });
            } else {
              setNewBookList({ ...newBookList, name: e.target.value });
            }
          }}
          className="post-title-input"
        />
        <textarea
          placeholder="Description (optional)..."
          value={currentForm.description}
          onChange={(e) => {
            if (isEditing) {
              setEditBookListForm({ ...editBookListForm, description: e.target.value });
            } else {
              setNewBookList({ ...newBookList, description: e.target.value });
            }
          }}
          className="post-content-input"
          rows="3"
        />

        {/* Add Books to List */}
        <div className="form-group">
          <label>Add Books to List:</label>
          <div className="form-row">
            <input
              type="text"
              placeholder="Book Title"
              value={newBookEntry.title}
              onChange={(e) => setNewBookEntry({ ...newBookEntry, title: e.target.value })}
              className="post-title-input"
              style={{ flex: 1 }}
            />
            <input
              type="text"
              placeholder="Author"
              value={newBookEntry.author}
              onChange={(e) => setNewBookEntry({ ...newBookEntry, author: e.target.value })}
              className="post-title-input"
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="proposal-edit-btn"
              onClick={handleAddBook}
            >
              Add Book
            </button>
          </div>
          
          {/* Bulk Import Option */}
          <div style={{ marginTop: '12px' }}>
            {!showBulkImport ? (
              <button
                type="button"
                className="proposal-edit-btn"
                onClick={() => setShowBulkImport(true)}
                style={{ width: 'auto' }}
              >
                Bulk Import Books
              </button>
            ) : (
              <div style={{ marginTop: '12px', padding: '12px', border: '1px solid var(--color-border-primary)', borderRadius: '4px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-semibold)' }}>
                  Bulk Import (one per line, format: "Title, Author"):
                </label>
                <textarea
                  value={bulkBooksInput}
                  onChange={(e) => setBulkBooksInput(e.target.value)}
                  placeholder={`The Great Gatsby, F. Scott Fitzgerald
To Kill a Mockingbird, Harper Lee
1984, George Orwell`}
                  className="post-content-input"
                  rows="8"
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    className="log-activity-button"
                    onClick={handleBulkImportBooks}
                  >
                    Import Books
                  </button>
                  <button
                    type="button"
                    className="proposal-cancel-btn"
                    onClick={() => {
                      setBulkBooksInput('');
                      setShowBulkImport(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Books in List */}
        {currentBooks.length > 0 && (
          <div className="book-list-books">
            <label>Books in this list ({currentBooks.length}):</label>
            <ul className="book-list-items">
              {[...currentBooks].sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase())).map((book) => (
                <li key={book.id} className="book-list-item">
                  <span>{book.title} by {book.author}</span>
                  <button
                    type="button"
                    className="proposal-reject-btn"
                    onClick={() => handleRemoveBook(book.id)}
                    style={{ marginLeft: '10px', padding: '2px 8px' }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="proposal-actions">
          {isEditing ? (
            <>
              <button className="proposal-save-btn" onClick={handleSaveBookListEdit}>Save Changes</button>
              <button className="proposal-cancel-btn" onClick={handleCancelEditBookList}>Cancel</button>
            </>
          ) : (
            <button className="log-activity-button" onClick={handleAddBookList}>Create Book List</button>
          )}
        </div>
      </div>

      {/* Existing Book Lists */}
      <div className="time-logs-container">
        <h3>Existing Book Lists</h3>
        {bookLists.length === 0 ? (
          <p className="no-posts">No book lists created yet.</p>
        ) : (
          <div className="time-logs-list">
            {bookLists.map(bookList => (
              <div key={bookList.id} className="consolidated-time-log-entry">
                <div className="consolidated-time-log-main">
                  <h5>{bookList.name}</h5>
                  {bookList.description && (
                    <p className="individual-description">{bookList.description}</p>
                  )}
                  <div className="book-list-summary">
                    <span>{bookList.books?.length || 0} {bookList.books?.length === 1 ? 'book' : 'books'} in list</span>
                  </div>
                  {bookList.books && bookList.books.length > 0 && (
                    <ul className="book-list-items" style={{ marginTop: '10px' }}>
                      {[...(bookList.books || [])].sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase())).map((book, idx) => (
                        <li key={book.id || idx} className="book-list-item">
                          {book.title} by {book.author}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="proposal-actions">
                  <button className="proposal-edit-btn" onClick={() => handleStartEditBookList(bookList)}>Edit</button>
                  <button className="proposal-reject-btn" onClick={() => handleDeleteBookList(bookList.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Book List Progress View Component (for students)
export const BookListProgressView = ({
  bookLists,
  bookLogs,
  currentUser
}) => {
  // State for tracking expanded book lists
  const [expandedProgressLists, setExpandedProgressLists] = useState(new Set());
  
  const toggleProgressListExpansion = (listId) => {
    setExpandedProgressLists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  };
  
  const isProgressListExpanded = (listId) => {
    return expandedProgressLists.has(listId);
  };
  
  const userBookLogs = bookLogs.filter(log => log.userId === currentUser.uid);

  // Helper function to normalize text for comparison (handles capitalization differences)
  const normalizeText = (text) => {
    if (!text) return '';
    return String(text).toLowerCase().trim().replace(/\s+/g, ' ');
  };

  // Helper function to normalize author name (handle order variations)
  const normalizeAuthor = (author) => {
    if (!author) return '';
    const normalized = normalizeText(author);
    // Split by comma or space and get parts
    return normalized.split(/[, ]+/).filter(part => part.length > 0);
  };

  // Helper function to check if two author names match (flexible - partial matches allowed)
  const authorsMatch = (author1Parts, author2Parts) => {
    if (author1Parts.length === 0 || author2Parts.length === 0) return false;
    
    // If either is a subset of the other, they match
    // Check if all parts of author1 are in author2 or vice versa
    const allParts1In2 = author1Parts.every(part => 
      author2Parts.some(part2 => part2.includes(part) || part.includes(part2))
    );
    const allParts2In1 = author2Parts.every(part => 
      author1Parts.some(part1 => part1.includes(part) || part.includes(part1))
    );
    
    return allParts1In2 || allParts2In1;
  };

  // Helper function to check if two book entries match (handles capitalization differences)
  const booksMatch = (book1, book2) => {
    // Normalize both titles and authors to handle capitalization differences
    const title1 = normalizeText(book1.title || '');
    const title2 = normalizeText(book2.title || '');
    const author1Parts = normalizeAuthor(book1.author || '');
    const author2Parts = normalizeAuthor(book2.author || '');
    
    // Title must match exactly (case-insensitive, normalized)
    if (title1 !== title2) return false;
    
    // Authors match if they have overlapping parts (flexible partial matching)
    return authorsMatch(author1Parts, author2Parts);
  };

  const getProgressForList = (bookList) => {
    const listBooks = bookList.books || [];
    const loggedBooks = userBookLogs.filter(log => 
      log.bookListIds && log.bookListIds.includes(bookList.id)
    );

    // Match logged books with list books (flexible matching)
    const matchedBooks = listBooks.filter(listBook => {
      return loggedBooks.some(loggedBook => booksMatch(loggedBook, listBook));
    });

    // Create list of all books with completion status, sorted alphabetically by title
    const booksWithStatus = listBooks.map(listBook => {
      const isCompleted = loggedBooks.some(loggedBook => booksMatch(loggedBook, listBook));
      return {
        ...listBook,
        isCompleted
      };
    }).sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()));

    return {
      total: listBooks.length,
      completed: matchedBooks.length,
      percentage: listBooks.length > 0 ? Math.round((matchedBooks.length / listBooks.length) * 100) : 0,
      booksWithStatus
    };
  };

  if (bookLists.length === 0) {
    return (
      <div className="time-logs-container time-logging-section">
        <h2>Book List Progress</h2>
        <p className="no-posts">No book lists available yet.</p>
      </div>
    );
  }

  return (
    <div className="time-logs-container time-logging-section">
      <h2>Book List Progress</h2>
      <p>Track your progress through assigned reading lists.</p>
      
      <div className="time-logs-list">
        {bookLists.map(bookList => {
          const progress = getProgressForList(bookList);
          const isExpanded = isProgressListExpanded(bookList.id);
          
          return (
            <div key={bookList.id} className="consolidated-time-log-entry">
              <div className="book-list-content">
                <div 
                  className="book-list-progress-header"
                  onClick={() => toggleProgressListExpansion(bookList.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={`book-list-progress-arrow ${isExpanded ? 'expanded' : ''}`}>
                    ▶
                  </div>
                  <div className="book-list-progress-header-content">
                    <div className="consolidated-time-log-main">
                      <h5>{bookList.name}</h5>
                    </div>
                    {bookList.description && (
                      <p className="individual-description">{bookList.description}</p>
                    )}
                    <div className="book-list-progress">
                      <div className="progress-bar-container">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${progress.percentage}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {progress.completed} of {progress.total} books completed ({progress.percentage}%)
                      </span>
                    </div>
                  </div>
                </div>
                {isExpanded && bookList.books && bookList.books.length > 0 && (
                  <div className="book-list-all-books">
                    <strong>Books in this list:</strong>
                    <ul className="book-list-items" style={{ marginTop: '8px' }}>
                      {progress.booksWithStatus.map((book, idx) => (
                        <li 
                          key={book.id || idx} 
                          className={`book-list-item ${book.isCompleted ? 'book-list-item-completed' : ''}`}
                        >
                          <span className="book-list-item-check">
                            {book.isCompleted ? (
                              <i className="fa-solid fa-check" style={{ color: 'var(--color-success)' }}></i>
                            ) : (
                              <i className="fa-regular fa-circle" style={{ color: 'var(--color-text-tertiary)' }}></i>
                            )}
                          </span>
                          <span className="book-list-item-text">
                            {book.title} by {book.author}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Book List Progress Overview Component (for teachers)
export const BookListProgressOverview = ({
  bookLists,
  bookLogs,
  students
}) => {
  // State for expanded book lists per student
  const [expandedBookLists, setExpandedBookLists] = useState({});

  const toggleBookListExpansion = (studentId, listId) => {
    setExpandedBookLists(prev => {
      const key = `${studentId}-${listId || 'my-books'}`;
      const newExpanded = { ...prev };
      newExpanded[key] = !newExpanded[key];
      return newExpanded;
    });
  };

  const isBookListExpanded = (studentId, listId) => {
    const key = `${studentId}-${listId || 'my-books'}`;
    return expandedBookLists[key] || false;
  };
  // Helper function to normalize text for comparison (reused from BookListProgressView)
  const normalizeText = (text) => {
    return text.toLowerCase().trim().replace(/\s+/g, ' ');
  };

  // Helper function to normalize author name (handle order variations)
  const normalizeAuthor = (author) => {
    if (!author) return '';
    const normalized = normalizeText(author);
    // Split by comma or space and get parts
    return normalized.split(/[, ]+/).filter(part => part.length > 0);
  };

  // Helper function to check if two author names match (flexible - partial matches allowed)
  const authorsMatch = (author1Parts, author2Parts) => {
    if (author1Parts.length === 0 || author2Parts.length === 0) return false;
    
    // If either is a subset of the other, they match
    // Check if all parts of author1 are in author2 or vice versa
    const allParts1In2 = author1Parts.every(part => 
      author2Parts.some(part2 => part2.includes(part) || part.includes(part2))
    );
    const allParts2In1 = author2Parts.every(part => 
      author1Parts.some(part1 => part1.includes(part) || part.includes(part1))
    );
    
    return allParts1In2 || allParts2In1;
  };

  // Helper function to check if two book entries match (handles capitalization differences)
  const booksMatch = (book1, book2) => {
    // Normalize both titles and authors to handle capitalization differences
    const title1 = normalizeText(book1.title || '');
    const title2 = normalizeText(book2.title || '');
    const author1Parts = normalizeAuthor(book1.author || '');
    const author2Parts = normalizeAuthor(book2.author || '');
    
    // Title must match exactly (case-insensitive, normalized)
    if (title1 !== title2) return false;
    
    // Authors match if they have overlapping parts (flexible partial matching)
    return authorsMatch(author1Parts, author2Parts);
  };

  // Get progress for a student on a specific book list
  const getStudentProgressForList = (studentId, bookList) => {
    const listBooks = bookList.books || [];
    const studentBookLogs = bookLogs.filter(log => 
      log.userId === studentId &&
      log.bookListIds && 
      log.bookListIds.includes(bookList.id)
    );

    // Count how many books from the list the student has completed
    const completedCount = listBooks.filter(listBook => {
      return studentBookLogs.some(loggedBook => booksMatch(loggedBook, listBook));
    }).length;

    // Get all books the student has read (matching and non-matching from this list)
    // Sort alphabetically by title
    const studentReadBooks = studentBookLogs
      .sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()));

    return {
      total: listBooks.length,
      completed: completedCount,
      percentage: listBooks.length > 0 ? Math.round((completedCount / listBooks.length) * 100) : 0,
      readBooks: studentReadBooks
    };
  };

  if (bookLists.length === 0) {
    return (
      <div className="time-logs-container time-logging-section">
        <h2>Book List Progress Overview</h2>
        <p className="no-posts">No book lists created yet.</p>
      </div>
    );
  }

  if (!students || students.length === 0) {
    return (
      <div className="time-logs-container time-logging-section">
        <h2>Book List Progress Overview</h2>
        <p className="no-posts">No students available.</p>
      </div>
    );
  }

  const sortedStudents = [...students].sort((a, b) => {
    const nameA = (a.firstName || a.name || a.id || '').toLowerCase();
    const nameB = (b.firstName || b.name || b.id || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="time-logs-container time-logging-section">
      <h2>Book List Progress Overview</h2>
      <p className="transcript-description">
        View progress for all students across all reading lists.
      </p>
      
      <div className="time-logs-list">
        {/* Organize by Student, then by Reading List */}
        {sortedStudents.map(student => {
          // Get all books for this student, grouped by list
          const studentBooks = bookLogs.filter(log => log.userId === student.id);
          
          // Get books for each reading list
          const studentBookLists = bookLists.map(bookList => {
            const progress = getStudentProgressForList(student.id, bookList);
            return {
              bookList,
              progress,
              hasBooks: progress.readBooks && progress.readBooks.length > 0
            };
          }).filter(item => item.hasBooks); // Only show lists where student has books
          
          // Get My Books for this student
          const studentMyBooks = studentBooks.filter(log => 
            !log.bookListIds || log.bookListIds.length === 0
          ).sort((a, b) => (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase()));
          
          // Skip students with no books
          if (studentBookLists.length === 0 && studentMyBooks.length === 0) {
            return null;
          }
          
          return (
            <div key={student.id} className="consolidated-time-log-entry">
              <div className="book-list-content">
                <div className="consolidated-time-log-main">
                  <h5>{student.firstName || student.name || student.id}</h5>
                </div>
                
                {/* Reading Lists for this Student */}
                {studentBookLists.map(({ bookList, progress }) => {
                  const isExpanded = isBookListExpanded(student.id, bookList.id);
                  
                  return (
                    <div key={bookList.id} className="grade-section" style={{ marginTop: '16px' }}>
                      <div 
                        className="grade-header"
                        onClick={() => toggleBookListExpansion(student.id, bookList.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={`grade-arrow ${isExpanded ? 'expanded' : ''}`}>
                          ▶
                        </div>
                        <h3 className="grade-title">
                          {bookList.name}
                          <span style={{ marginLeft: '8px', fontWeight: 'normal', fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
                            ({progress.readBooks.length} {progress.readBooks.length === 1 ? 'book' : 'books'})
                          </span>
                        </h3>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div className="book-list-progress-bar-small" style={{ width: '100px' }}>
                            <div 
                              className="book-list-progress-bar-fill-small"
                              style={{ width: `${progress.percentage}%` }}
                            ></div>
                          </div>
                          <span className="book-list-progress-count" style={{ fontSize: '0.9em' }}>
                            {progress.completed} / {progress.total}
                          </span>
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div className="time-logs-list" style={{ marginTop: '8px' }}>
                          {progress.readBooks.map(bookLog => (
                            <div key={bookLog.id} className="consolidated-time-log-entry">
                              <div className="consolidated-time-log-main">
                                <div className="book-log-title-row">
                                  <h5>{bookLog.title}</h5>
                                  {bookLog.rating && (
                                    <div className="book-rating-inline">
                                      <span className="rating-stars">
                                        {Array.from({ length: parseInt(bookLog.rating) }, (_, i) => (
                                          <i key={`filled-${i}`} className="fa-solid fa-star"></i>
                                        ))}
                                        {Array.from({ length: 5 - parseInt(bookLog.rating) }, (_, i) => (
                                          <i key={`empty-${i}`} className="fa-regular fa-star"></i>
                                        ))}
                                      </span>
                                      <span> ({bookLog.rating}/5)</span>
                                    </div>
                                  )}
                                </div>
                                  {bookLog.author && (
                                    <p className="book-author-text"><em>by {bookLog.author}</em></p>
                                  )}
                                  {bookLog.review && (
                                  <p className="individual-description" style={{ marginTop: '8px' }}>{bookLog.review}</p>
                                )}
                                <span className="individual-dates" style={{ display: 'block', marginTop: '8px' }}>
                                  Logged: {new Date(bookLog.timestamp).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* My Books for this Student */}
                {studentMyBooks.length > 0 && (
                  <div className="grade-section" style={{ marginTop: studentBookLists.length > 0 ? '16px' : '0' }}>
                    <div 
                      className="grade-header"
                      onClick={() => toggleBookListExpansion(student.id, null)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className={`grade-arrow ${isBookListExpanded(student.id, null) ? 'expanded' : ''}`}>
                        ▶
                      </div>
                      <h3 className="grade-title">
                        My Books
                        <span style={{ marginLeft: '8px', fontWeight: 'normal', fontSize: '0.9em', color: 'var(--color-text-secondary)' }}>
                          ({studentMyBooks.length} {studentMyBooks.length === 1 ? 'book' : 'books'})
                        </span>
                      </h3>
                    </div>
                    
                    {isBookListExpanded(student.id, null) && (
                      <div className="time-logs-list" style={{ marginTop: '8px' }}>
                        {studentMyBooks.map(bookLog => (
                          <div key={bookLog.id} className="consolidated-time-log-entry">
                            <div className="consolidated-time-log-main">
                              <div className="book-log-title-row">
                                <h5>{bookLog.title}</h5>
                                {bookLog.rating && (
                                  <div className="book-rating-inline">
                                    <span className="rating-stars">
                                      {Array.from({ length: parseInt(bookLog.rating) }, (_, i) => (
                                        <i key={`filled-${i}`} className="fa-solid fa-star"></i>
                                      ))}
                                      {Array.from({ length: 5 - parseInt(bookLog.rating) }, (_, i) => (
                                        <i key={`empty-${i}`} className="fa-regular fa-star"></i>
                                      ))}
                                    </span>
                                    <span> ({bookLog.rating}/5)</span>
                                  </div>
                                )}
                              </div>
                                  {bookLog.author && (
                                    <p className="book-author-text"><em>by {bookLog.author}</em></p>
                                  )}
                                  {bookLog.review && (
                                <p className="individual-description" style={{ marginTop: '8px' }}>{bookLog.review}</p>
                              )}
                              <span className="individual-dates" style={{ display: 'block', marginTop: '8px' }}>
                                Logged: {new Date(bookLog.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const bookLoggingComponents = {
  BookLogCreationForm,
  BookLogDisplay,
  useBookLogging,
  bookLoggingUtils,
  useBookListManagement,
  BookListManagementView,
  BookListProgressView,
  BookListProgressOverview
};

export default bookLoggingComponents;

